// components/ContextWarning.js
import React, { useState } from 'react';
import './ContextWarning.css';

/**
 * Enhanced component to display a warning when approaching context limits
 * with added tooltips for better context understanding
 */
function ContextWarning({ isVisible, contextLevel, percentage, onOpenSettings }) {
  const [showTooltip, setShowTooltip] = useState(false);
  
  if (!isVisible) return null;
  
  // Format the context level for display
  const formatContextLevel = (level) => {
    switch(level) {
      case 'maximum':
        return 'Maximum (100%)';
      case 'standard':
        return 'Standard (50%)';
      case 'minimum':
        return 'Minimum (20%)';
      default:
        return level;
    }
  };
  
  // Get description for the context level
  const getContextDescription = (level) => {
    switch(level) {
      case 'maximum':
        return 'Uses the full available context window, allowing Claude to remember as much of the conversation as possible. Higher token usage may result in slightly slower responses.';
      case 'standard':
        return 'Uses approximately half of the maximum context, balancing memory with performance. Claude will remember important parts of the conversation but may forget older messages.';
      case 'minimum':
        return 'Uses only 20% of the available context, optimizing for speed. Claude will focus primarily on your most recent messages.';
      default:
        return 'Controls how much of your conversation history is included in each request to Claude.';
    }
  };
  
  // Get color based on percentage
  const getColorClass = () => {
    if (percentage > 90) return 'danger';
    if (percentage > 70) return 'warning';
    return 'normal';
  };
  
  return (
    <div className="context-warning">
      <div className="context-warning-icon">⚠️</div>
      <div className="context-warning-content">
        <div className="context-warning-text">
          <p>
            You're approaching the context limit ({percentage}%) at your current{' '}
            <span 
              className="context-level-indicator"
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              {formatContextLevel(contextLevel)}
              {showTooltip && (
                <span className="context-tooltip">
                  {getContextDescription(contextLevel)}
                </span>
              )}
            </span>{' '}
            setting. Some older messages may be excluded from future responses.
          </p>
          <button 
            className="context-settings-link"
            onClick={onOpenSettings}
          >
            Change Context Settings
          </button>
        </div>
      </div>
      <div className="context-warning-progress">
        <div 
          className={`context-warning-progress-bar ${getColorClass()}`}
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
    </div>
  );
}

export default ContextWarning;